import React, { useState } from "react";

import { Chip, Stack, Typography } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useGetBenchmarkLiquidityGraph } from "queries/query-selectors/benchmark-query-selectors";
import { useGraphBenchmarkValuesSelect } from "store";
import { BenchmarkLiquidityGraphParams } from "store/types/reports/benchmark";

import { MultipleAssetEditPopup } from "./MultipleAssetEditPopup";

const years = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];

interface Props {
  title?: string;
  isFreemium?: boolean;
}

const assetClasses = [
  { id: "buyout", label: "Buyout" },
  { id: "venture_capital", label: "Venture Capital" },
  { id: "secondary", label: "Secondary" },
  { id: "debt", label: "Debt" }
];

export const LiquidityGraph: React.FC<Props> = ({ title, isFreemium = false }) => {
  const graphBenchmarkValues = useGraphBenchmarkValuesSelect();

  const [values, setValues] = useState<BenchmarkLiquidityGraphParams>(graphBenchmarkValues);

  const { data, isLoading } = useGetBenchmarkLiquidityGraph(values);

  const handleApply = (newValues: BenchmarkLiquidityGraphParams) => {
    setValues(newValues);
  };

  const options: Highcharts.Options = {
    chart: {
      type: "spline",
      style: {
        fontFamily: "Roboto, sans-serif"
      },
      height: isFreemium ? 285 : 400,
      spacingLeft: 0,
      spacingRight: 0,
      marginLeft: 60,
      marginRight: 20,
      events: {
        load: function () {
          this.credits?.element?.remove();
        }
      }
    },
    title: {
      text: title || "",
      style: {
        color: "#0A3142",
        fontSize: "16px",
        fontWeight: "500"
      }
    },
    xAxis: isFreemium
      ? {
          visible: false
        }
      : {
          categories: years,
          title: {
            text: "Years",
            style: {
              color: "#8895A7",
              fontSize: "12px"
            }
          },
          tickInterval: 4,
          gridLineWidth: 0,
          gridLineColor: "#EAEDEF",
          minPadding: 0,
          maxPadding: 0,
          labels: {
            formatter: function () {
              return years[Math.floor(this.pos / 4)];
            },
            style: {
              color: "#8895A7",
              fontSize: "12px"
            }
          },
          startOnTick: true,
          endOnTick: true,
          tickmarkPlacement: "on",
          alignTicks: false,
          offset: 0,
          lineWidth: 0,
          tickLength: 0
        },
    yAxis: {
      title: {
        text: "X of commitment",
        align: "middle",
        rotation: 270,
        style: {
          color: "#8895A7",
          fontSize: "12px"
        },
        x: -40
      },
      gridLineColor: "#EAEDEF",
      gridLineWidth: 1,
      labels: {
        style: {
          color: "#8895A7",
          fontSize: "12px"
        }
      },
      min: -2,
      max: 2,
      tickAmount: 9,
      startOnTick: true,
      endOnTick: true,
      minPadding: 0,
      maxPadding: 0
    },
    exporting: {
      enabled: false
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      x: 0,
      y: 0,
      symbolWidth: -2,
      symbolHeight: 0,
      symbolRadius: 0,
      itemDistance: 20,
      margin: 15,
      padding: 0,
      itemStyle: {
        color: "#0A3142",
        fontSize: "14px",
        fontWeight: "normal"
      },
      labelFormatter: function () {
        const isHidden = !this.visible;
        // const opacity = isHidden ? "0.4" : "1";
        const color = isHidden ? "#8895a7" : this.color;
        const symbol = `<span style="
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: ${color};
          margin-right: 5px;
          vertical-align: middle;
          transition: all 0.2s ease;
        "></span>`;
        return `<span style="
          display: flex;
          align-items: center;
          transition: all 0.2s ease;
          color: ${isHidden ? "#8895a7" : "#0A3142"};
        ">${symbol}${this.name}</span>`;
      },
      useHTML: true,
      symbolPadding: 0,
      itemMarginBottom: 5,
      itemHoverStyle: {
        color: "#0A3142"
      },
      itemHiddenStyle: {
        color: "#8895A7"
      }
    },
    plotOptions: {
      spline: {
        marker: {
          enabled: false,
          symbol: "circle",
          states: {
            hover: {
              enabled: true,
              radius: 2
            }
          }
        },
        states: {
          hover: {
            lineWidth: 3
          }
        },
        lineWidth: 1.5,
        clip: false
      },
      series: {
        pointPlacement: "on"
      }
    },
    series: [
      {
        name: "Distribution",
        data: data?.distribution,
        color: "#4690F5",
        type: "spline"
      },
      {
        name: "Net Cashflow",
        data: data?.netCashflow,
        color: "#32CED8",
        type: "spline"
      },
      {
        name: "Capital Call",
        data: data?.capitalCall,
        color: "#D428BD",
        type: "spline"
      }
    ],
    credits: {
      enabled: false
    },
    tooltip: {
      shared: true,
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      borderColor: "#DBE8F5",
      borderRadius: 8,
      shadow: false,
      style: {
        fontSize: "12px"
      },
      headerFormat: "",
      footerFormat: "",
      formatter: function () {
        if (!this.points) return "";

        const pointIndex = this.points[0].point.index;
        const yearIndex = Math.floor(pointIndex / 4);
        const quarterIndex = pointIndex % 4;
        const year = years[yearIndex];
        const label = quarterIndex === 3 ? `Year - ${year}` : `Q${quarterIndex + 1}`;

        let s = `<b>${label}</b><br/>`;

        this.points.forEach((point) => {
          s += `<span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${(point as any).y.toFixed(2)}</b><br/>`;
        });

        return s;
      }
    }
  };

  const content = (
    <>
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        gap="10px"
        sx={{
          flexWrap: "wrap",
          rowGap: "8px"
        }}
      >
        <Typography sx={{ fontSize: "14px", color: "#0A3142", fontWeight: 500, whiteSpace: "nowrap" }}>
          Asset Class:
        </Typography>
        {Object.entries(values).map(([key, value]) => (
          <Chip
            key={key}
            label={
              <Stack direction="row" gap="5px">
                <Typography component="span" fontSize="12px">
                  {assetClasses.find((asset) => asset.id === key)?.label}
                </Typography>
                <Typography component="span" sx={{ fontWeight: 600, fontSize: "12px" }}>
                  {value}%
                </Typography>
              </Stack>
            }
            sx={{
              height: "26px",
              fontSize: "14px",
              color: "#0A3142",
              backgroundColor: "#F6F8FC",
              flexShrink: 0
            }}
          />
        ))}
        <MultipleAssetEditPopup initialValues={values} onApply={handleApply} isFreemium={isFreemium} />
      </Stack>
      <AsyncContentMui isLoading={isLoading} boxProps={{ sx: { height: isFreemium ? 285 : 400 } }}>
        <Stack sx={{ ...(isFreemium && { maxHeight: "401px", overflow: "hidden" }), height: isFreemium ? 285 : 400 }}>
          <HighchartsReact highcharts={Highcharts} options={options} />
        </Stack>
      </AsyncContentMui>
    </>
  );

  if (isFreemium) {
    return <Stack sx={{ width: "100%", maxHeight: "401px", overflow: "hidden" }}>{content}</Stack>;
  }

  return (
    <Stack
      sx={{
        backgroundColor: "white",
        p: "16px",
        borderRadius: "8px",
        boxShadow: "0 1px 2px 0 #1212171a",
        width: "100%"
      }}
    >
      {content}
    </Stack>
  );
};
